import type { ReactNode } from 'react';
import { useCallback, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import {
  type DrawerMenuItem,
  type Group,
  type ItemsType,
  type MobileHeaderPageStackItem,
  navItemsType,
  type NavItemType,
  navItemType,
  type SiteConfig,
  type SubLink,
  type SublinkItem,
} from '@cx-web/domain-types';
import classnames from 'classnames';

import PromoCodeDropdown from '../Menu/PromoCodeDropdown';
import SplitNavLinks from '../SplitNavLinks/index';

import styles from './SubComponent.module.css';

interface SubComponentProps {
  bookmakers: SiteConfig['bookmakers'];
  callback?: (text: string) => void;
  groups: Group[];
  items: SublinkItem[];
  itemsType?: ItemsType;
  label: string;
  name: string;
  pageClick: (item: MobileHeaderPageStackItem) => void;
  renderMenuLinks?: (pages: DrawerMenuItem[], label: string) => ReactNode;
  serverUrl: string;
  showLinksPage: boolean;
  subLinks: SubLink[];
  type: NavItemType;
}

export const SubComponent = ({
  bookmakers,
  callback,
  groups,
  items,
  itemsType,
  label,
  name,
  pageClick,
  renderMenuLinks,
  serverUrl,
  showLinksPage,
  subLinks,
  type,
}: SubComponentProps) => {
  const nodeRef = useRef(null);

  const isPromoCodesDropdown = itemsType === navItemsType.PROMO_CODE;
  const isSplitDropdown = type === navItemType.SPLIT_EXPANDABLE;

  const styleClass = isSplitDropdown ? styles.pageSplitNav : isPromoCodesDropdown ? styles.pagePromoCodes : '';
  const classes = classnames(styles.page, styleClass);

  const subComponent = useCallback(() => {
    switch (true) {
      case !!isSplitDropdown:
        return <SplitNavLinks links={groups} serverUrl={serverUrl} />;

      case !!isPromoCodesDropdown:
        return (
          <PromoCodeDropdown
            bookmakers={bookmakers}
            callback={callback}
            label={`${label} - ${name}`}
            links={items}
            serverUrl={serverUrl}
          />
        );

      default:
        return renderMenuLinks?.(subLinks, `${label} - ${name}`) ?? null;
    }
  }, [
    callback,
    groups,
    items,
    label,
    name,
    pageClick,
    renderMenuLinks,
    serverUrl,
    subLinks,
    isPromoCodesDropdown,
    isSplitDropdown,
  ]);

  return (
    <CSSTransition
      classNames={{
        enter: styles.pageEnter,
        enterActive: styles.pageEnterActive,
        enterDone: styles.visible,
        exit: styles.pageExit,
        exitActive: styles.pageExitActive,
      }}
      in={showLinksPage}
      nodeRef={nodeRef}
      timeout={1000}
    >
      <div className={classes} ref={nodeRef}>
        {subComponent()}
      </div>
    </CSSTransition>
  );
};
